/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - Semantic Model Definition
 * Feature #183: External Metadata Enrichment
 * Constitution: v1.18.0
 * Conformed to Spec 197 design blueprint
 */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Database, Eye, EyeOff, Link2, ShieldCheck } from 'lucide-react';
import { useSemanticModelDefinition } from '../../services/operations/metadataService';
import {
  PageLayout,
  Card,
  DataTable,
  Badge,
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import type {
  SemanticModelTable,
  SemanticModelColumn,
  SemanticModelMeasure,
  SemanticModelRelationship,
  SemanticModelRole,
} from '@shared/types/metadata';

export const SemanticModelDefinitionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState(0);
  const { data, isLoading, isError, refetch } = useSemanticModelDefinition(id ?? null);

  if (isLoading) {
    return (
      <PageLayout
      helpId="auto.semantic-model-definition" title="Semantic Model" icon={<Database className="h-6 w-6" />}>
        <Card>
          <LoadingState rows={6} />
        </Card>
      </PageLayout>
    );
  }

  if (isError) {
    return (
      <PageLayout title="Semantic Model" icon={<Database className="h-6 w-6" />}>
        <ErrorState
          title="Failed to load definition"
          message="The semantic model definition could not be loaded. Please try again."
          onRetry={() => void refetch()}
        />
      </PageLayout>
    );
  }

  if (!data) {
    return (
      <PageLayout title="Semantic Model" icon={<Database className="h-6 w-6" />}>
        <Card>
          <EmptyState
            icon={<Database className="h-10 w-10" />}
            title="Model not found"
            message="This semantic model does not exist or has not been collected."
          />
        </Card>
      </PageLayout>
    );
  }

  const hasDefinition = data.tables.length > 0 || data.measures.length > 0;

  // ── Column configs ───────────────────────────────────────────────────────────

  const columnColumns: DataTableColumn<SemanticModelColumn>[] = [
    {
      key: 'columnName',
      header: 'Column',
      width: '35%',
      render: (c) => <span className="font-medium text-[var(--color-text-primary)]">{c.columnName}</span>,
    },
    {
      key: 'dataType',
      header: 'Type',
      width: '25%',
      render: (c) => c.dataType ?? <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'isHidden',
      header: 'Hidden',
      width: '15%',
      render: (c) =>
        c.isHidden ? (
          <EyeOff className="h-4 w-4 text-[var(--color-text-secondary)]" />
        ) : (
          <Eye className="h-4 w-4 text-[var(--color-status-success)]" />
        ),
    },
  ];

  const measureColumns: DataTableColumn<SemanticModelMeasure>[] = [
    {
      key: 'measureName',
      header: 'Measure',
      width: '20%',
      render: (m) => <span className="font-medium text-[var(--color-text-primary)]">{m.measureName}</span>,
    },
    { key: 'tableName', header: 'Table', width: '15%' },
    {
      key: 'daxExpression',
      header: 'DAX Expression',
      width: '40%',
      render: (m) =>
        m.daxExpression ? (
          <pre className="overflow-x-auto rounded bg-[var(--color-bg-subtle)] px-2 py-1 text-xs">
            <code>{m.daxExpression}</code>
          </pre>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
    {
      key: 'isHidden',
      header: 'Hidden',
      width: '10%',
      render: (m) =>
        m.isHidden ? (
          <EyeOff className="h-4 w-4 text-[var(--color-text-secondary)]" />
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
  ];

  const relationshipColumns: DataTableColumn<SemanticModelRelationship>[] = [
    {
      key: 'from',
      header: 'From',
      width: '25%',
      render: (r) => (
        <span className="text-[var(--color-text-primary)]">
          {r.fromTable}[{r.fromColumn}]
        </span>
      ),
    },
    {
      key: 'to',
      header: 'To',
      width: '25%',
      render: (r) => (
        <span className="flex items-center gap-1 text-[var(--color-text-primary)]">
          <Link2 className="h-3 w-3 flex-shrink-0 text-[var(--color-text-secondary)]" />
          {r.toTable}[{r.toColumn}]
        </span>
      ),
    },
    {
      key: 'cardinality',
      header: 'Cardinality',
      width: '17%',
      render: (r) => r.cardinality ?? <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'crossFilterDirection',
      header: 'Cross-filter',
      width: '18%',
      render: (r) =>
        r.crossFilterDirection ?? <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'isActive',
      header: 'Active',
      width: '15%',
      render: (r) => (
        <Badge variant={r.isActive ? 'success' : 'secondary'} isOutlined>
          {r.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  const roleColumns: DataTableColumn<SemanticModelRole>[] = [
    {
      key: 'roleName',
      header: 'Role',
      width: '25%',
      render: (r) => (
        <span className="flex items-center gap-1 font-medium text-[var(--color-text-primary)]">
          <ShieldCheck className="h-4 w-4 flex-shrink-0 text-[var(--color-accent)]" />
          {r.roleName}
        </span>
      ),
    },
    {
      key: 'modelPermission',
      header: 'Permission',
      width: '20%',
      render: (r) => r.modelPermission ?? <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'tableFilterDax',
      header: 'Table Filter DAX',
      width: '55%',
      render: (r) =>
        r.tableFilterDax ? (
          <pre className="overflow-x-auto rounded bg-[var(--color-bg-subtle)] px-2 py-1 text-xs">
            <code>{r.tableFilterDax}</code>
          </pre>
        ) : (
          <span className="text-[var(--color-text-secondary)]">None</span>
        ),
    },
  ];

  const tabs = [
    `Tables / Columns (${data.tables.length})`,
    `Measures (${data.measures.length})`,
    `Relationships (${data.relationships.length})`,
    `RLS Roles (${data.roles.length})`,
  ];

  return (
    <PageLayout
      title={data.displayName}
      description={`Workspace: ${data.workspaceName ?? '—'}`}
      icon={<Database className="h-6 w-6" />}
    >
      {!hasDefinition && (
        <Card className="mb-6">
          <EmptyState
            icon={<Database className="h-10 w-10" />}
            title="Definition not yet collected"
            message="Run the daily scanner collector to populate tables, measures, and relationships."
          />
        </Card>
      )}

      {/* Tabs */}
      <div className="mb-4 flex gap-1 border-b border-[var(--color-border)]">
        {tabs.map((label, i) => (
          <button
            key={label}
            onClick={() => setTab(i)}
            className={clsx(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              tab === i
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tables / Columns */}
      {tab === 0 &&
        (data.tables.length === 0 ? (
          <Card>
            <EmptyState title="No tables collected" />
          </Card>
        ) : (
          <div className="space-y-4">
            {data.tables.map((t: SemanticModelTable) => (
              <Card key={t.id} padding="none">
                <div className="flex items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-4 py-2">
                  <span className="font-semibold text-[var(--color-text-primary)]">{t.tableName}</span>
                  {t.tableType && (
                    <Badge variant="secondary" isOutlined>
                      {t.tableType}
                    </Badge>
                  )}
                </div>
                <DataTable
                  columns={columnColumns}
                  rows={t.columns}
                  rowKey={(c) => c.id}
                  emptyMessage="No columns collected"
                  minWidth="44rem"
                />
              </Card>
            ))}
          </div>
        ))}

      {/* Measures */}
      {tab === 1 && (
        <Card padding="none">
          <DataTable
            columns={measureColumns}
            rows={data.measures}
            rowKey={(m) => m.id}
            emptyMessage="No measures collected"
            minWidth="44rem"
          />
        </Card>
      )}

      {/* Relationships */}
      {tab === 2 && (
        <Card padding="none">
          <DataTable
            columns={relationshipColumns}
            rows={data.relationships}
            rowKey={(r) => r.id}
            emptyMessage="No relationships collected"
            minWidth="44rem"
          />
        </Card>
      )}

      {/* RLS Roles */}
      {tab === 3 && (
        <Card padding="none">
          <DataTable
            columns={roleColumns}
            rows={data.roles}
            rowKey={(r) => r.id}
            emptyMessage="No RLS roles defined"
            minWidth="44rem"
          />
        </Card>
      )}
    </PageLayout>
  );
};

export default SemanticModelDefinitionPage;
